import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { LearnChip } from "@/pages/Library";
import { DemoToolbar } from "@/components/DemoToolbar";
import { Link, Navigate, Outlet, useNavigate, useLocation, useParams } from "react-router-dom";
import { Eye, Menu, ShieldCheck } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, statusOf } from "@/lib/api";
import { canSee } from "@/lib/nav";
import { Loader, NoAccess } from "@/components/common";
import { HouseQ } from "@/components/brand/Brand";
import Sidebar from "@/components/nav/Sidebar";
import Screensaver from "@/components/look/Screensaver";
import { useLook } from "@/context/LookContext";
import { LiveProvider } from "@/lib/live";
import { CallLayer } from "@/pages/VideoCalls";
import { NotificationsProvider } from "@/lib/notifications";
import NotificationBell from "@/components/notify/NotificationBell";
import CopilotPanel from "@/components/copilot/CopilotPanel";
import IosInstallBanner from "@/components/notify/IosInstallHint";
import { useLiveRefresh } from "@/lib/live";
import { api as apiClient } from "@/lib/api";

const HUB_EVENTS = ["chore", "bill", "member", "organization", "wish", "message", "conversation"];

// Keeps sidebar counts and the person's look in sync with events from other screens/devices.
function LiveShellSync({ reloadHub }) {
  const { replace } = useLook();
  useLiveRefresh(() => reloadHub(), (m) => HUB_EVENTS.includes(m.entity), null);
  useLiveRefresh(() => { if (!document.hasFocus()) apiClient.get("/me/look").then(({ data }) => replace(data)).catch(() => {}); }, (m) => m.type === "look.updated", null);
  return null;
}

export const HubCtx = createContext(null);
export const useHub = () => useContext(HubCtx);

export function SectionGuard({ section, children }) {
  const { hub } = useHub();
  return canSee(section, hub) ? children : <NoAccess />;
}

function HubDenied({ message }) {
  const { session } = useAuth();
  const nav = useNavigate();
  const other = session?.hubs?.[0];
  const admin = session?.user?.platform_role;
  return (
    <div className="relative min-h-screen bg-q-bg px-5">
      <NoAccess message={message} action={(admin || other) && (
        <button className="q-btn q-btn-primary" data-testid="no-access-go-hub" onClick={() => nav(admin ? "/admin/home" : `/h/${other.id}/home`)}>
          {admin ? "Back to Admin" : `Go to ${other.name}`}
        </button>
      )} />
    </div>
  );
}

function ViewBanner({ hub }) {
  if (hub?.demo) {
    return (<>
      <DemoToolbar hub={hub} />
      {hub.plan?.status === "lapsed" && <div className="px-4 py-2 text-center text-sm font-bold" data-testid="lapsed-banner"
        style={{ background: "rgba(239,68,68,0.14)", color: "rgb(var(--st-red))", borderBottom: "1px solid rgba(239,68,68,0.35)" }}>This hub is read-only. Families see "Renew to unlock" here.</div>}
    </>);
  }
  if (hub?.plan?.status === "lapsed" && hub.view_mode === "member") {
    return (
      <div className="flex flex-wrap items-center justify-center gap-2 px-4 py-2.5 text-sm font-bold" data-testid="lapsed-banner"
        style={{ background: "rgba(239,68,68,0.14)", color: "rgb(var(--st-red))", borderBottom: "1px solid rgba(239,68,68,0.35)" }}>
        This hub is read-only.{" "}
        {hub.my_role === "owner" ? <Link to={`/h/${hub.id}/family/billing`} className="inline-flex min-h-[44px] items-center underline underline-offset-2" data-testid="lapsed-renew-link">Renew to unlock</Link>
          : <span data-testid="lapsed-ask-owner">Ask {hub.owner_first_name} to renew</span>}
      </div>
    );
  }
  if (hub?.view_mode === "support") {
    return (
      <div className="flex items-center justify-center gap-2 px-4 py-2 text-xs font-bold uppercase tracking-[0.14em]" data-testid="support-view-banner"
        style={{ background: "rgba(var(--glow-admin),0.16)", color: "rgb(var(--glow-admin))", borderBottom: "1px solid rgba(var(--glow-admin),0.35)" }}>
        <Eye className="h-3.5 w-3.5" /> Support view · read-only · {hub.name}
      </div>
    );
  }
  if (hub?.view_mode === "demo") {
    return (
      <div className="flex items-center justify-center gap-2 px-4 py-2 text-xs font-bold uppercase tracking-[0.14em]" data-testid="demo-view-banner"
        style={{ background: "rgba(var(--glow-admin),0.1)", color: "rgb(var(--glow-admin))", borderBottom: "1px solid rgba(var(--glow-admin),0.25)" }}>
        <ShieldCheck className="h-3.5 w-3.5" /> Previewing demo family ·{" "}
        <Link to="/admin/home" className="underline underline-offset-2 hover:text-white" data-testid="demo-back-to-admin-link">Back to Admin</Link>
      </div>
    );
  }
  return null;
}

export function ShellLayout({ hub, title }) {
  const { pathname } = useLocation();
  const [drawer, setDrawer] = useState(false);
  const { look } = useLook();
  const wp = look?.wallpaper;
  useEffect(() => setDrawer(false), [pathname]);
  return (
    <div className="relative min-h-screen bg-q-bg" data-testid="app-shell" data-theme={look?.theme} data-has-wallpaper={wp && wp.type !== "none" ? "1" : "0"}>
      <div className="q-wallpaper" data-testid="app-background-layer" data-wallpaper={wp?.type === "none" || !wp ? "none" : `${wp.type}:${wp.id}`} data-dim={look?.dim} />
      <div className="relative z-10 flex h-screen">
        <aside className="q-chrome hidden h-full w-[232px] flex-shrink-0 flex-col border-r border-q-border bg-q-bg md:flex" data-testid="hub-sidebar">
          <Sidebar hub={hub} />
        </aside>
        <div className="flex min-w-0 flex-1 flex-col">
          <ViewBanner hub={hub} />
          <header className="flex items-center gap-3 border-b border-q-border bg-q-bg/90 px-4 py-3 backdrop-blur-md md:hidden">
            <button className="q-btn q-btn-ghost -ml-2 px-2" onClick={() => setDrawer(true)} data-testid="mobile-menu-btn" aria-label="Open menu">
              <Menu className="h-5 w-5" />
            </button>
            <HouseQ size={24} />
            <span className="min-w-0 flex-1 truncate font-display text-sm font-bold text-q-wordmark">{title}</span>
            {hub && <CopilotPanel hubId={hub.id} testid="ask-copilot-btn-mobile" />}
            {hub && <NotificationBell testid="notification-bell-mobile" />}
          </header>
          {hub && (
            <header className="hidden h-14 flex-shrink-0 items-center justify-end gap-2 border-b border-q-border bg-q-bg/70 px-6 backdrop-blur-md md:flex" data-testid="app-header">
              <CopilotPanel hubId={hub.id} />
              <NotificationBell />
            </header>
          )}
          {hub && <IosInstallBanner />}
          <main className="flex-1 overflow-y-auto overflow-x-hidden" data-testid="app-main">
            <div className="mx-auto w-full max-w-6xl px-4 py-8 sm:px-8 sm:py-10">
              {hub?.type === "personal" && <div className="mb-4 flex flex-wrap items-center gap-2 rounded-2xl bg-q-raised p-3 text-sm text-q-text" data-testid="private-space-banner">Private to you. No one else in your roommates hub can see this.
                <a href={`/h/${hub.parent_hub_id}/roommates`} className="font-semibold text-q-accent" data-testid="private-space-back">Back to shared bills</a></div>}
              {hub && <LearnChip />}
              <Outlet />
              <CallLayer hub={hub} enabled={!!hub?.calls_enabled} />
            </div>
          </main>
        </div>
      </div>
      <Sheet open={drawer} onOpenChange={setDrawer}>
        <SheetContent side="left" className="q-chrome w-[264px] border-q-border bg-q-bg p-0 [&>button]:hidden" data-testid="mobile-drawer">
          <SheetTitle className="sr-only">Menu</SheetTitle>
          <div className="flex h-full flex-col"><Sidebar hub={hub} onNavigate={() => setDrawer(false)} /></div>
        </SheetContent>
      </Sheet>
      <Screensaver hub={hub} />
    </div>
  );
}

export function AdminShell() {
  const { session } = useAuth();
  if (session === null) return <div className="min-h-screen bg-q-bg"><Loader /></div>;
  if (!session) return <Navigate to="/signin" replace />;
  if (!session.user?.platform_role) return <div className="min-h-screen bg-q-bg px-5"><NoAccess /></div>;
  return <ShellLayout hub={null} title="Admin" />;
}

export default function AppShell() {
  const { hubId } = useParams();
  const { pathname } = useLocation();
  const { session, refresh } = useAuth();
  const [hub, setHub] = useState(null);
  const [denied, setDenied] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/organizations/${hubId}`);
      setHub(data);
      setDenied(null);
    } catch (e) {
      if (statusOf(e) === 403) {
        setDenied(errMsg(e));
        setHub(null);
        refresh();
      }
    }
  }, [hubId, refresh]);

  // Re-check membership + access on every page change so access edits apply on the next load.
  useEffect(() => {
    if (session) load();
  }, [session ? 1 : 0, load, pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  if (session === false) return <Navigate to="/signin" replace />;
  if (denied) return <HubDenied message={denied} />;
  if (!session || !hub || hub.id !== hubId) return <div className="min-h-screen bg-q-bg"><Loader /></div>;

  return (
    <HubCtx.Provider value={{ hub, reloadHub: load }}>
      <LiveProvider hubId={hub.id} key={hub.id}>
        <LiveShellSync reloadHub={load} />
        <NotificationsProvider>
          <ShellLayout hub={hub} title={hub.name} />
        </NotificationsProvider>
      </LiveProvider>
    </HubCtx.Provider>
  );
}
